import React, { memo, useRef, useCallback } from 'react';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import L, { LatLngExpression } from 'leaflet';
import { GeoJsonObject } from 'geojson';
import { MapContainer, TileLayer } from 'react-leaflet';
import classNames from 'classnames';
import { GeojsonProperties } from 'graphql-common';
import getChipTypesOptions from '@lib/components/Chip/utils/getChipTypesOptions';
import { t } from 'i18next';
import { MapSize } from './enums';
import { MapParams, PopupContentType } from './types';
import {
  DEFAULT_ZOOM,
  PROVIDER_URL,
  ATTRIBUTION,
  DEFAULT_CENTER,
} from './constants';
import MapInner from './MapInner';
import styles from './Map.module.scss';
import './leaflet.scss';
import './leaflet.draw.scss';

const pointerDefaultProps = {
  addressDetails: null,
  shape: 'circle',
  color: getChipTypesOptions({ t })[0].value as string,
  address: null,
  icon: null,
  id: null,
  pointCount: null,
  sourceables: null,
  type: null,
};

function Map({
  bordered = true,
  defaultCenter,
  disableAutoFit,
  hasError,
  mapParams,
  mapRef,
  maxMarkers = 0,
  maxPolygons = 0,
  onCenterMap,
  onChange,
  pointerProps = pointerDefaultProps,
  polygonColor = '#214EEB',
  polygonDashArray = '5, 5',
  polygonFillOpacity = 0.4,
  PopupContent,
  setMapParams,
  showDrowTool,
  size = MapSize.Medium,
  topRightOverlay,
  value: geoJson,
}: {
  bordered?: boolean;
  defaultCenter?: LatLngExpression | null;
  disableAutoFit?: boolean;
  hasError?: boolean;
  mapParams?: MapParams | null;
  mapRef?: React.RefObject<L.Map | null>;
  maxMarkers?: number;
  maxPolygons?: number;
  onCenterMap?: () => void;
  onChange?: (value: GeoJsonObject) => void;
  pointerProps?: GeojsonProperties;
  polygonColor?: string;
  polygonDashArray?: string | number[];
  polygonFillOpacity?: number;
  PopupContent?: PopupContentType;
  setMapParams?: (params: MapParams | null) => void;
  showDrowTool?: boolean;
  size?: MapSize;
  topRightOverlay?: React.ReactNode;
  value: GeoJsonObject;
}) {
  const featureGroupRef = useRef<L.FeatureGroup>(null);
  const mapParamsRef = useRef(mapParams);
  mapParamsRef.current = mapParams;

  const center = (_get(mapParams, 'center') ||
    defaultCenter ||
    DEFAULT_CENTER) as LatLngExpression;
  const zoom = _get(mapParams, 'zoom') || DEFAULT_ZOOM;

  const updateMapParams = useCallback(
    (newMapParams: MapParams | null) => {
      if (setMapParams && !_isEqual(newMapParams, mapParamsRef.current)) {
        setMapParams(newMapParams);
      }
    },
    [setMapParams],
  );

  return (
    <div
      className={classNames(styles.mapWrapper, {
        [styles.hasError]: hasError,
        [styles.hasFullHeight]: size === MapSize.Full,
      })}
    >
      {topRightOverlay ? (
        <div className={styles.topRightOverlay}>{topRightOverlay}</div>
      ) : null}
      <MapContainer
        ref={mapRef}
        center={center}
        zoom={zoom}
        minZoom={2}
        maxBounds={[
          [-90, -180],
          [90, 180],
        ]}
        maxBoundsViscosity={1.0}
        worldCopyJump
        className={classNames(styles.map, {
          [styles.mapBordered]: bordered,
          [styles.mapSmall]: size === MapSize.Small,
          [styles.mapMedium]: size === MapSize.Medium,
          [styles.mapLarge]: size === MapSize.Large,
          [styles.mapFull]: size === MapSize.Full,
        })}
        scrollWheelZoom={false}
      >
        <TileLayer url={PROVIDER_URL} attribution={ATTRIBUTION} />
        <MapInner
          disableAutoFit={disableAutoFit}
          featureGroupRef={featureGroupRef}
          geoJson={geoJson}
          maxMarkers={maxMarkers}
          maxPolygons={maxPolygons}
          onCenterMap={onCenterMap}
          onChange={onChange}
          pointerProps={pointerProps}
          polygonColor={polygonColor}
          polygonDashArray={polygonDashArray}
          polygonFillOpacity={polygonFillOpacity}
          PopupContent={PopupContent}
          setMapParams={updateMapParams}
          showDrowTool={showDrowTool}
        />
      </MapContainer>
    </div>
  );
}

export default memo(Map);
